import type { OpenItem, OpenItemStatus, OpenItemTopic } from '../api/dto.js';
import { statusRank } from './status.js';

/** `member:<userId>` / `guest:<guestId>` – the same keys as comment mentions. */
export const personKey = (p: { kind: string; id: string }): string => `${p.kind}:${p.id}`;

/** Assigned to me or created by me ("mine" filter). */
export function isMyOpenItem(item: OpenItem, me: string): boolean {
  return (
    (item.assignee !== null && personKey(item.assignee) === me) || personKey(item.originator) === me
  );
}

/** "Concerns me" (decision C3): assigned, originator, replied or mentioned in a reply. */
export function concernsMe(item: OpenItem, me: string): boolean {
  return (
    isMyOpenItem(item, me) ||
    item.replies.some((r) => personKey(r.author) === me || r.mentions.includes(me))
  );
}

/** `active` = everything but resolved (the default: resolved items are hidden). */
export type OpenItemStatusFilter = OpenItemStatus | 'active' | 'all';

export interface OpenItemFilter {
  status: OpenItemStatusFilter;
  /** Person key, `unassigned`, or `null` for everyone. */
  assignee: string | null;
  topic: OpenItemTopic | null;
  mine: boolean;
}

export const DEFAULT_OPEN_ITEM_FILTER: OpenItemFilter = {
  status: 'active',
  assignee: null,
  topic: null,
  mine: false,
};

export const UNASSIGNED = 'unassigned';

function statusMatches(s: OpenItemStatus, f: OpenItemStatusFilter): boolean {
  if (f === 'all') return true;
  return f === 'active' ? s !== 'resolved' : s === f;
}

function assigneeMatches(item: OpenItem, f: string | null): boolean {
  if (f === null) return true;
  return item.assignee === null ? f === UNASSIGNED : personKey(item.assignee) === f;
}

export function filterOpenItems(
  items: readonly OpenItem[],
  f: OpenItemFilter,
  me: string | null,
): OpenItem[] {
  return items.filter(
    (i) =>
      statusMatches(i.status, f.status) &&
      assigneeMatches(i, f.assignee) &&
      (f.topic === null || i.topic === f.topic) &&
      (!f.mine || (me !== null && isMyOpenItem(i, me))),
  );
}

export type OpenItemSortKey =
  'default' | 'title' | 'status' | 'assignee' | 'topic' | 'due' | 'updated';

const text = (a: string, b: string) => a.localeCompare(b);
/** Missing values sort last in both directions. */
function nullsLast(a: string | null, b: string | null, dir: number): number {
  if (a === b) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return dir * text(a, b);
}

function primary(a: OpenItem, b: OpenItem, key: OpenItemSortKey, dir: number): number {
  switch (key) {
    case 'title':
      return dir * text(a.title, b.title);
    case 'assignee':
      return nullsLast(a.assignee?.name ?? null, b.assignee?.name ?? null, dir);
    case 'topic':
      return dir * text(a.topic, b.topic);
    case 'due':
      return nullsLast(a.dueDate, b.dueDate, dir);
    case 'updated':
      return dir * text(a.updatedAt, b.updatedAt);
    case 'status':
      return dir * (statusRank(a.status) - statusRank(b.status));
    case 'default':
      return statusRank(a.status) - statusRank(b.status) || nullsLast(a.dueDate, b.dueDate, 1);
  }
}

/**
 * Stable sort. `default`: status workflow order, then due date (undated last); ties by creation
 * time and id, so the order never jumps between polls.
 */
export function sortOpenItems(
  items: readonly OpenItem[],
  key: OpenItemSortKey = 'default',
  dir: 'asc' | 'desc' = 'asc',
): OpenItem[] {
  const d = dir === 'asc' ? 1 : -1;
  return [...items].sort(
    (a, b) => primary(a, b, key, d) || text(a.createdAt, b.createdAt) || text(a.id, b.id),
  );
}

/** Next/previous item id (wrapping); starts at the first/last when `current` is not listed. */
export function stepOpenItem(
  ids: readonly string[],
  current: string | null,
  dir: 1 | -1,
): string | null {
  if (ids.length === 0) return null;
  const i = current === null ? -1 : ids.indexOf(current);
  if (i < 0) return dir === 1 ? ids[0]! : ids[ids.length - 1]!;
  return ids[(i + dir + ids.length) % ids.length]!;
}
